import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import CustomFieldsRenderer from "../../components/CustomFieldsRenderer";
import { Plus, Trash2, X, ClipboardList, KeyRound, Copy, UserCog } from "lucide-react";

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", full_name: "", company_name: "", role: "customer" });
  const [error, setError] = useState("");
  const [resetResult, setResetResult] = useState(null); // { email, password }
  const [profileEditUser, setProfileEditUser] = useState(null); // user being edited
  const [profileEditForm, setProfileEditForm] = useState(null); // form state
  const [profileEditSaving, setProfileEditSaving] = useState(false);
  const [assessments, setAssessments] = useState([]);
  const load = () => { setLoading(true); api.getUsers().then(setUsers).catch(console.error).finally(() => setLoading(false)); };
  useEffect(() => {
    load();
    api.getPublicAssessments().then(setAssessments).catch(() => {});
  }, []);

  // Aggregate non-consent custom fields across all assessments — used in the
  // "Profile Data" editor modal.
  const aggregatedFields = useMemo(() => {
    const map = new Map();
    for (const a of assessments) {
      const cf = a.company_info_schema?.custom_fields || [];
      for (const f of cf) {
        if ((f.type || '').toLowerCase() === 'consent') continue;
        if (!map.has(f.key)) map.set(f.key, f);
      }
    }
    return Array.from(map.values());
  }, [assessments]);

  const handleResetPassword = async (user) => {
    if (!window.confirm(`Reset password for ${user.email}?\n\nA new temporary password will be generated and shown once.`)) return;
    try {
      const res = await api.request(`/admin/users/${user.id}/reset-password`, { method: "POST" });
      setResetResult({ email: res.email, password: res.temp_password });
    } catch (err) { alert(err.message); }
  };

  const handleEditProfile = (user) => {
    setProfileEditUser(user);
    setProfileEditForm({
      full_name: user.full_name || '',
      company_name: user.company_name || '',
      contact_phone: user.contact_phone || '',
      profile_extras: user.profile_extras || {},
    });
  };

  const handleSaveProfile = async () => {
    if (!profileEditUser) return;
    setProfileEditSaving(true);
    try {
      await api.updateUser(profileEditUser.id, profileEditForm);
      setProfileEditUser(null);
      load();
    } catch (err) { alert(err.message); }
    setProfileEditSaving(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault(); setError("");
    try { await api.createUser(form); setShowCreate(false); setForm({ email: "", password: "", full_name: "", company_name: "", role: "customer" }); load(); }
    catch (err) { setError(err.message); }
  };

  const roleBadge = (r) => {
    const c = { admin: "bg-red-50 text-red-600", agent: "bg-blue-50 text-blue-600", customer: "bg-gray-100 text-gray-600" };
    return <span className={`px-2 py-1 rounded text-xs font-medium ${c[r] || c.customer}`}>{r}</span>;
  };

  const inputCls = "w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-emerald-500";

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Users</h1>
        <button data-testid="create-user-btn" onClick={() => setShowCreate(true)} className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium"><Plus className="w-4 h-4" /> Add User</button>
      </div>
      {showCreate && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
          <div className="bg-white border border-gray-200 rounded-xl p-6 w-full max-w-md mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4"><h2 className="text-lg font-bold text-gray-900">Create User</h2><button onClick={() => setShowCreate(false)} className="text-gray-400"><X className="w-5 h-5" /></button></div>
            {error && <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded mb-4 text-sm">{error}</div>}
            <form onSubmit={handleCreate} className="space-y-4">
              <input data-testid="user-email-input" type="email" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputCls} required />
              <input data-testid="user-password-input" type="password" placeholder="Password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className={inputCls} required />
              <input data-testid="user-name-input" placeholder="Full Name" value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} className={inputCls} required />
              <input placeholder="Company (optional)" value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} className={inputCls} />
              <select data-testid="user-role-select" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className={inputCls}>
                <option value="customer">Customer</option><option value="agent">Agent</option><option value="admin">Admin</option>
              </select>
              <button data-testid="user-submit-btn" type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2.5 rounded-lg">Create</button>
            </form>
          </div>
        </div>
      )}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? <div className="p-8 text-center text-gray-400">Loading...</div> : (
          <table className="w-full">
            <thead><tr className="border-b border-gray-200 bg-gray-50">
              <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase">Role</th>
              <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="text-right px-6 py-4 text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">{u.full_name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{u.email}</td>
                  <td className="px-6 py-4">{roleBadge(u.role)}</td>
                  <td className="px-6 py-4"><span className={`px-2 py-1 rounded text-xs font-medium ${u.is_active ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"}`}>{u.is_active ? "Active" : "Inactive"}</span></td>
                  <td className="px-6 py-4 text-right">
                    <div className="inline-flex items-center gap-2">
                      {u.role === 'customer' && (
                        <button
                          data-testid={`initiate-for-user-${u.id}`}
                          onClick={() => navigate(`/admin/initiate?customer_id=${u.id}`)}
                          title="Initiate assessment for this customer"
                          className="text-emerald-600 hover:text-emerald-800 p-1.5 hover:bg-emerald-50 rounded">
                          <ClipboardList className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        data-testid={`edit-profile-${u.id}`}
                        onClick={() => handleEditProfile(u)}
                        title="View / edit profile data"
                        className="text-blue-600 hover:text-blue-800 p-1.5 hover:bg-blue-50 rounded">
                        <UserCog className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`reset-password-${u.id}`}
                        onClick={() => handleResetPassword(u)}
                        title="Reset password"
                        className="text-amber-600 hover:text-amber-800 p-1.5 hover:bg-amber-50 rounded">
                        <KeyRound className="w-4 h-4" />
                      </button>
                      <button onClick={() => { if (window.confirm("Delete?")) api.deleteUser(u.id).then(load); }} className="text-red-400 hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-400">No users found</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      {/* Admin profile-data editor modal */}
      {profileEditUser && profileEditForm && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onClick={() => setProfileEditUser(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl p-6 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="profile-edit-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Profile Data — {profileEditUser.full_name}</h3>
                <p className="text-xs text-gray-500">{profileEditUser.email}</p>
              </div>
              <button onClick={() => setProfileEditUser(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Full Name</label>
                  <input value={profileEditForm.full_name} onChange={e => setProfileEditForm({ ...profileEditForm, full_name: e.target.value })}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Phone</label>
                  <input value={profileEditForm.contact_phone} onChange={e => setProfileEditForm({ ...profileEditForm, contact_phone: e.target.value })}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Company</label>
                <input value={profileEditForm.company_name} onChange={e => setProfileEditForm({ ...profileEditForm, company_name: e.target.value })}
                  className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              {aggregatedFields.length > 0 ? (
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-xs text-gray-500 mb-3">Custom fields aggregated from all assessments. Saved on the user's profile and pre-fills future assessments.</p>
                  <CustomFieldsRenderer
                    fields={aggregatedFields}
                    values={profileEditForm.profile_extras || {}}
                    onChange={(k, v) => setProfileEditForm(prev => ({ ...prev, profile_extras: { ...(prev.profile_extras || {}), [k]: v } }))}
                  />
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">No custom company-info fields defined on any assessment yet.</p>
              )}
              <div className="flex gap-2 pt-2">
                <button onClick={handleSaveProfile} disabled={profileEditSaving} data-testid="save-profile-edit-btn"
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-lg font-semibold disabled:opacity-50">
                  {profileEditSaving ? 'Saving...' : 'Save Changes'}
                </button>
                <button onClick={() => setProfileEditUser(null)} className="px-4 py-2 rounded-lg text-sm text-gray-600 bg-gray-100 hover:bg-gray-200">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* One-time temp password modal after admin reset */}
      {resetResult && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" data-testid="reset-password-result-modal">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Password Reset</h3>
            <p className="text-sm text-gray-500 mb-4">Share this temporary password with the user. <b>It is shown only once.</b></p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
              <p className="text-xs text-gray-500 mb-0.5">Email</p>
              <p className="text-sm font-mono text-gray-900 mb-2 break-all">{resetResult.email}</p>
              <p className="text-xs text-gray-500 mb-0.5">Temporary Password</p>
              <div className="flex items-center gap-2">
                <p className="text-sm font-mono text-gray-900 flex-1 break-all">{resetResult.password}</p>
                <button onClick={() => navigator.clipboard.writeText(resetResult.password)}
                  className="text-emerald-600 hover:text-emerald-700 p-1.5 hover:bg-emerald-50 rounded">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
            <button onClick={() => setResetResult(null)} data-testid="reset-password-confirm"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-lg font-semibold">
              I've copied it — Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
